import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, MapPin, Clock, ChevronDown } from 'lucide-react';
import { laverieInfo } from '../data/mock';
import BasqueFlag from './BasqueFlag';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu when scrolling
  useEffect(() => {
    if (isMenuOpen) {
      const handleScroll = () => setIsMenuOpen(false);
      document.addEventListener('scroll', handleScroll);
      return () => {
        document.removeEventListener('scroll', handleScroll);
      };
    }
  }, [isMenuOpen]);

  const navLinks = [
    { href: '#services', label: 'Services' },
    { href: '#marches', label: 'Votre Secteur' },
    { href: '#tarifs', label: 'Tarifs' },
    { href: '#galerie', label: 'Galerie' },
    { href: '#contact', label: 'Contact' }
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      isScrolled || isMenuOpen ? 'bg-black/95 backdrop-blur-lg shadow-2xl' : 'bg-transparent'
    }`}>
      {/* Top bar */}
      <div className={`transition-all duration-500 ${isScrolled ? 'h-0 overflow-hidden opacity-0' : 'h-auto opacity-100'}`}>
        <div className="bg-gradient-to-r from-[#9B1B30] to-[#006633] py-2 px-4">
          <div className="max-w-7xl mx-auto flex flex-wrap justify-between items-center text-sm gap-2">
            <div className="flex items-center gap-6 flex-wrap">
              <a href={`tel:${laverieInfo.phone.replace(/\s/g, '')}`} className="flex items-center gap-2 text-white hover:text-white/80 transition-colors">
                <Phone size={14} />
                <span className="font-medium">{laverieInfo.phone}</span>
              </a>
              <span className="hidden md:flex items-center gap-2 text-white/90">
                <MapPin size={14} />
                <span>{laverieInfo.address}, {laverieInfo.city}</span>
              </span>
            </div>
            <div className="flex items-center gap-2 text-white font-semibold">
              <Clock size={14} />
              <span>{laverieInfo.hours.days} • {laverieInfo.hours.open} - {laverieInfo.hours.close}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <nav className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <a href="#" className="flex items-center gap-3 group">
            <div className="w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-[#9B1B30] to-[#7A1526] rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow">
              <span className="text-white font-black text-lg md:text-xl">LR</span>
            </div>
            <div className="hidden sm:block">
              <h1 className="text-lg md:text-xl font-bold text-white leading-tight">Lavoir des Remparts</h1>
              <div className="flex items-center gap-2">
                <p className="text-xs text-[#9B1B30] font-bold tracking-wider">LAVERIE 3.0</p>
                <BasqueFlag size={16} />
              </div>
            </div>
          </a>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-white/80 hover:text-white font-medium transition-colors relative group py-2"
              >
                {link.label}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#9B1B30] transition-all duration-300 group-hover:w-full" />
              </a>
            ))}
            <a
              href={`tel:${laverieInfo.phone.replace(/\s/g, '')}`}
              className="bg-[#9B1B30] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#B91C3B] transition-all shadow-lg hover:shadow-xl hover:scale-105"
            >
              Appeler
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 text-white hover:text-[#9B1B30] transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Menu"
          >
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile nav - Full screen overlay with solid black background */}
        {isMenuOpen && (
          <div 
            className="lg:hidden fixed left-0 right-0 bottom-0 z-50"
            style={{ 
              top: '80px',
              backgroundColor: '#000000',
              minHeight: 'calc(100vh - 80px)'
            }}
            data-testid="mobile-menu-overlay"
          >
            <div className="px-6 py-8 space-y-4 bg-black">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="block text-white text-xl font-semibold py-4 border-b border-white/20 hover:text-[#9B1B30] transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {link.label}
                </a>
              ))}
              <a
                href={`tel:${laverieInfo.phone.replace(/\s/g, '')}`}
                className="block bg-[#9B1B30] text-white px-6 py-4 rounded-full font-semibold text-center text-lg hover:bg-[#B91C3B] transition-colors mt-8"
                onClick={() => setIsMenuOpen(false)}
                data-testid="mobile-call-button"
              >
                Appeler maintenant
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
